import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Download, Sparkles, Send } from "lucide-react";

export const MAKE_WEBHOOK = "https://hook.eu1.make.com/dzqx93j4uqgypi2fkovojxs1a95wncsv";

export type LeadKind = "diag" | "material" | "direct";
export type LeadConfig = { kind: LeadKind; title: string; text: string; button: string; link?: string };

export const LEAD_DEFAULTS: Record<LeadKind, LeadConfig> = {
  diag: { kind: "diag", title: "Diagnóstico rápido e gratuito", text: "Conte sobre sua marca e receba uma análise da nossa equipe.", button: "Quero meu diagnóstico" },
  material: { kind: "material", title: "Baixe o material completo", text: "Receba o guia prático direto no seu e-mail.", button: "Baixar agora", link: "" },
  direct: { kind: "direct", title: "Quer conversar com um especialista?", text: "Deixe seu WhatsApp e retornamos em breve.", button: "Quero contato" },
};

export const LEAD_LABELS: Record<LeadKind, string> = {
  diag: "Diagnóstico rápido",
  material: "Material rico",
  direct: "Chamada direta",
};

const SEGMENTS = ["Petshop", "Suplementos", "Produtos naturais", "Saúde & bem-estar", "Serviços", "Outro"];

export const encodeLead = (c: LeadConfig) => encodeURIComponent(JSON.stringify(c));
export const decodeLead = (s: string): LeadConfig | null => {
  try { return JSON.parse(decodeURIComponent(s)); } catch { return null; }
};

const LeadForm = ({ config, article, position, preview }: { config: LeadConfig; article: string; position: number; preview?: boolean }) => {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [segment, setSegment] = useState(SEGMENTS[0]);
  const [sending, setSending] = useState(false);
  const Icon = config.kind === "material" ? Download : config.kind === "diag" ? Sparkles : Send;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (preview) { toast({ title: "Prévia", description: "Na página publicada, este formulário envia o lead." }); return; }
    setSending(true);
    try {
      const isEmail = contact.includes("@");
      await fetch(MAKE_WEBHOOK, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim() || undefined,
          email: (config.kind === "direct" ? (isEmail ? contact : "") : email).trim() || undefined,
          phone: config.kind === "direct" && !isEmail ? contact.trim() : undefined,
          segment: config.kind === "diag" ? segment : undefined,
          service: "Lead do Blog",
          form_type: LEAD_LABELS[config.kind],
          article,
          position,
          message: `Lead (${LEAD_LABELS[config.kind]}) no artigo "${article}", formulário nº ${position}`,
        }),
      });
      toast({ title: "Recebido!", description: config.kind === "material" ? "Seu material está liberado." : "Nossa equipe entrará em contato." });
      if (config.kind === "material" && config.link) window.open(config.link, "_blank", "noopener");
      setName(""); setEmail(""); setContact("");
    } catch {
      toast({ title: "Erro ao enviar", description: "Tente novamente em instantes.", variant: "destructive" });
    }
    setSending(false);
  };

  return (
    <div className="not-prose my-10 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card p-6 md:p-8 shadow-sm">
      <div className="flex items-start gap-3 mb-4">
        <span className="shrink-0 h-10 w-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center"><Icon size={20} /></span>
        <div>
          <h3 className="font-heading text-xl font-bold leading-tight">{config.title}</h3>
          {config.text && <p className="text-muted-foreground text-sm mt-1">{config.text}</p>}
        </div>
      </div>
      <form onSubmit={submit} className="flex flex-col sm:flex-row flex-wrap gap-3">
        {config.kind === "direct" ? (
          <Input className="flex-1 min-w-[200px]" placeholder="E-mail ou WhatsApp" value={contact} onChange={(e) => setContact(e.target.value)} required maxLength={255} />
        ) : (
          <>
            <Input className="flex-1 min-w-[160px]" placeholder="Seu nome" value={name} onChange={(e) => setName(e.target.value)} required maxLength={100} />
            <Input className="flex-1 min-w-[200px]" type="email" placeholder="Seu melhor e-mail" value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={255} />
            {config.kind === "diag" && (
              <select value={segment} onChange={(e) => setSegment(e.target.value)} className="h-10 px-3 rounded-md border border-input bg-background text-sm">
                {SEGMENTS.map((s) => <option key={s}>{s}</option>)}
              </select>
            )}
          </>
        )}
        <Button type="submit" disabled={sending} className="whitespace-nowrap">{sending ? "Enviando..." : config.button}</Button>
      </form>
    </div>
  );
};

export default LeadForm;
